import React, { useEffect, useState } from "react";
import Link from "next/link";
import moment from "moment";
import { useLazyQuery } from "@hooks/useLazyQuery";
import { GetComments } from "@graphql/configuration/queries/GetComments";
import SpinningLoader from "@components/loadingComponents/SpinningLoader";
import UserAvatar from "@components/userComponents/UserAvatar";
import { useRouter } from "next/router";

// Tailwind not working with template string for border colors, dont know if this is fixable, need to look into this more at some point for cleanliness.
const replyStyles = {
  commentDepth0: "ml-5 border-l text-[14px] border-[#AAADB1]",
  commentDepth1: "ml-5 border-l text-[14px] border-[#36C9C6]",
  commentDepth2: "ml-5 border-l text-[14px] border-[#F4F1BB]",
  commentDepth3: "ml-5 border-l text-[14px] border-[#a335ee]",
  commentDepth4: "ml-5 border-l text-[14px] border-[#1eff00]",
  commentDepth5: "ml-5 border-l text-[14px] border-[#F7F9F7]",
  commentDepth6: "ml-5 border-l text-[14px] border-[#F0F465]",
  commentDepth7: "ml-5 border-l text-[14px] border-[#F4AB7B]",
  commentDepth8: "ml-5 border-l text-[14px] border-[#F6C0D0]",
  commentDepth9: "ml-5 border-l text-[14px] border-[#B892FF]",
  commentDepth10: "ml-5 border-l text-[14px] border-[#EA4F3D]",
  commentDepth11: "ml-5 border-l text-[14px] border-[#B3DEC1]",
};

// Component that renders a single comment which also gets re used as a reply, also as a reply of a reply etc.
export default function Comment({
  post,
  comment,
  singleCommentMode = false,
  replyDepth = 0,
  avatarSize = "medium",
}) {
  const [fetchCommentReplies] = useLazyQuery(GetComments);
  const createdTimeAgo = moment.utc(comment.createdDate, "x").fromNow();
  const { user } = comment;
  const [isMoreReplies, setIsMoreReplies] = useState();
  const [fetchMoreReplies, setFetchMoreReplies] = useState();
  const [showReplyLoadingIndicator, setShowReplyLoadingIndicator] =
    useState(false);
  const router = useRouter();

  async function getCommentReplies({ decendent, maxDepth } = {}) {
    if (fetchMoreReplies) {
      fetchMoreReplies();
    } else {
      const { data, fetchNext, moreRepliesExtractor } =
        await fetchCommentReplies({
          variables: {
            post,
            comment,
            decendent,
            maxDepth,
          },
        });
      setShowReplyLoadingIndicator(false);
      setIsMoreReplies(moreRepliesExtractor(data));
      setFetchMoreReplies(() => fetchNext);
    }
  }

  useEffect(() => {
    const { replies, replyCount } = comment;
    if (replyCount > replies?.length) {
      setIsMoreReplies(true);
    }
  }, [comment]);

  function loadCommentReplies() {
    if (replyDepth > +10) {
      return;
    }
    setShowReplyLoadingIndicator(true);
    getCommentReplies({ maxDepth: 3 });
  }

  function renderMoreRepliesButton() {
    const { replies, replyCount } = comment;
    const repliesRemaining =
      replyDepth > +10 ? replyCount : replyCount - replies.length;
    if (isMoreReplies === false || repliesRemaining < 1) {
      return null;
    }
    return (
      <span
        className="text-[14px] hover:underline cursor-pointer pl-12 font-bold text-[#3a71b5]"
        aria-hidden
        onClick={() => loadCommentReplies()}
      >
        {" "}
        {repliesRemaining} {`${repliesRemaining === 1 ? "reply" : "replies"}`}
      </span>
    );
  }

  function renderCommentReplies() {
    const { replies, replyCount } = comment;

    if (replyCount === 0 || singleCommentMode === true) {
      return null;
    }

    const newReplyDepth = replyDepth + 1;
    const borderColor = `commentDepth${replyDepth}`;

    if (replyDepth > +10) {
      return <>{renderMoreRepliesButton()}</>;
    }

    return (
      <div>
        {showReplyLoadingIndicator ? (
          <div className="w-full flex justify-center overflow-hidden">
            <SpinningLoader size={4} />
          </div>
        ) : null}
        <div className={`${replyStyles[borderColor]}`}>
          {replies.map((commentItem, idx) => (
            <Comment
              key={commentItem.id}
              comment={commentItem}
              replyToComment={comment}
              post={post}
              replyDepth={newReplyDepth}
              index={idx}
              avatarSize="small"
            />
          ))}
        </div>
      </div>
    );
  }

  const handleAvatarClick = () => {
    router.push(`/user/${user.username}`);
  };

  return (
    <div className="flex flex-wrap max-w-full flex-col">
      <div className="py-3 px-2 flex flex-col max-w-full ">
        <div className="flex flex-row">
          {/* User Avatar */}
          <div>
            <UserAvatar
              handleClick={handleAvatarClick}
              user={user}
              active={comment.isActive}
              size={avatarSize}
            />
          </div>

          <div className="px-2 flex flex-col justify-center">
            {/* Comment User Info */}
            {comment.isActive ? (
              <div>
                <Link href={`/user/${user.username}`}>
                  <span className="font-bold mr-1 cursor-pointer">
                    {user.name}
                  </span>
                </Link>
                <Link href={`/user/${user.username}`}>
                  <span className="font-light text-[12.5px] cursor-pointer hover:underline">
                    @{user.username}
                  </span>
                </Link>
              </div>
            ) : null}
            {/* Comment Body */}
            <div className="break-all">
              {comment.isActive ? (
                <span>
                  {comment.commentBody}
                  <span className="text-gray-500 text-[11px] ml-1.5 break-all">
                    {createdTimeAgo}
                  </span>
                </span>
              ) : (
                <span className="text-gray-600 text-[15px]">
                  This comment has been removed.
                  <span className="text-gray-500 text-[11px] ml-1.5">
                    {createdTimeAgo}
                  </span>
                </span>
              )}
            </div>
          </div>
        </div>
        {showReplyLoadingIndicator ? null : renderMoreRepliesButton()}
      </div>
      {renderCommentReplies()}
    </div>
  );
}
